// verificari automate (OCR) pentru cererea incarcata
// toate rezultatele sunt doar avertismente: un document scanat prost poate fi corect,
// asa ca decizia finala apartine utilizatorului

// litere mici, fara diacritice si fara punctuatie
export const normalizeaza = (text) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const distanta = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return d[a.length][b.length];
};

// literele pe care OCR-ul le confunda des (I/l/1, O/0) sunt tratate ca fiind la fel
const canonic = (cuvant) => cuvant.replace(/[il1]/g, 'l').replace(/0/g, 'o');

// OCR-ul poate gresi o litera, asa ca la cuvintele mai lungi se accepta o diferenta
const cuvinteSimilare = (a, b) => {
  if (a === b || canonic(a) === canonic(b)) return true;
  if (Math.min(a.length, b.length) < 5) return false;
  return distanta(a, b) <= 1;
};

const cuvinte = (text) => normalizeaza(text).split(' ').filter(Boolean);

const gasesteCuvant = (cuvant, cuvinteText) =>
  cuvinteText.some((c) => cuvinteSimilare(cuvant, c));

// true daca toate partile numelui apar in text
export const contineNume = (text, nume) => {
  const parti = cuvinte(nume).filter((p) => p.length >= 2);
  if (parti.length === 0) return false;
  const cuvinteText = cuvinte(text);
  return parti.every((p) => gasesteCuvant(p, cuvinteText));
};

// procentul de cuvinte din titlu regasite in text (cuvintele scurte sunt ignorate)
export const potrivireTitlu = (text, titlu) => {
  const parti = cuvinte(titlu).filter((p) => p.length >= 3);
  if (parti.length === 0) return 0;
  const cuvinteText = cuvinte(text);
  const gasite = parti.filter((p) => gasesteCuvant(p, cuvinteText)).length;
  return gasite / parti.length;
};

export const PRAG_TITLU = 0.75;

// cuvintele recunoscute de tesseract, cu pozitia lor in imagine
export const cuvinteDinRezultat = (data) => {
  const rezultat = [];
  for (const bloc of data?.blocks || []) {
    for (const paragraf of bloc.paragraphs || []) {
      for (const linie of paragraf.lines || []) {
        for (const w of linie.words || []) {
          rezultat.push({ text: w.text, confidence: w.confidence, bbox: w.bbox });
        }
      }
    }
  }
  return rezultat;
};

// cauta eticheta formata din doua cuvinte consecutive (ex. „Semnătura student”)
export const gasesteEticheta = (cuvinteOcr, primul, alDoilea) => {
  for (let i = 0; i < cuvinteOcr.length - 1; i++) {
    const a = normalizeaza(cuvinteOcr[i].text);
    const b = normalizeaza(cuvinteOcr[i + 1].text);
    if (cuvinteSimilare(a, primul) && cuvinteSimilare(b, alDoilea)) {
      const b1 = cuvinteOcr[i].bbox;
      const b2 = cuvinteOcr[i + 1].bbox;
      return {
        x0: Math.min(b1.x0, b2.x0),
        y0: Math.min(b1.y0, b2.y0),
        x1: Math.max(b1.x1, b2.x1),
        y1: Math.max(b1.y1, b2.y1),
      };
    }
  }
  return null;
};

// in cerere, studentul semneaza sub eticheta „Semnătura student,”
export const zonaSemnaturaStudent = (eticheta, latime, inaltime) => {
  const h = eticheta.y1 - eticheta.y0;
  const w = eticheta.x1 - eticheta.x0;
  return limiteaza({
    x0: eticheta.x0 - w * 0.5,
    x1: eticheta.x1 + w * 0.5,
    y0: eticheta.y1 + h * 0.3,
    y1: eticheta.y1 + h * 3,
  }, latime, inaltime);
};

// coordonatorul semneaza deasupra etichetei „(semnătura coordonatorului și data)”
export const zonaSemnaturaCoordonator = (eticheta, latime, inaltime) => {
  const h = eticheta.y1 - eticheta.y0;
  return limiteaza({
    x0: eticheta.x0,
    x1: eticheta.x1,
    y0: eticheta.y0 - h * 3,
    y1: eticheta.y0 - h * 0.2,
  }, latime, inaltime);
};

const limiteaza = (z, latime, inaltime) => ({
  x0: Math.max(0, Math.round(z.x0)),
  y0: Math.max(0, Math.round(z.y0)),
  x1: Math.min(latime, Math.round(z.x1)),
  y1: Math.min(inaltime, Math.round(z.y1)),
});

const PRAG_INTUNECAT = 140;

// procentul de pixeli inchisi la culoare din zona, ignorand textul recunoscut
// si randurile de tip linie (ex. „__________” pentru semnatura)
export const procentCerneala = (pixeli, latime, zona, cutiiText = []) => {
  const { x0, y0, x1, y1 } = zona;
  const latimeZona = x1 - x0;
  if (latimeZona <= 0 || y1 - y0 <= 0) return 0;

  const inText = (x, y) =>
    cutiiText.some((b) => x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1);
  const intunecat = (x, y) => {
    const i = (y * latime + x) * 4;
    const lum = 0.299 * pixeli[i] + 0.587 * pixeli[i + 1] + 0.114 * pixeli[i + 2];
    return lum < PRAG_INTUNECAT;
  };

  let total = 0;
  let cerneala = 0;
  for (let y = y0; y < y1; y++) {
    let randIntunecat = 0;
    for (let x = x0; x < x1; x++) if (intunecat(x, y)) randIntunecat++;
    // un rand aproape complet inchis la culoare e o linie, nu o semnatura
    const eLinie = randIntunecat / latimeZona > 0.5;
    for (let x = x0; x < x1; x++) {
      if (inText(x, y)) continue;
      total++;
      if (!eLinie && intunecat(x, y)) cerneala++;
    }
  }
  return total === 0 ? 0 : cerneala / total;
};

export const PRAG_SEMNATURA = 0.004;

// sterge liniile orizontale lungi (sublinieri, „______”) dintr-o copie a imaginii,
// pentru ca OCR-ul sa nu piarda textul subliniat (titlul si numele profesorului);
// segmentele mai scurte decat lungimeMin (literele) raman neatinse
export const stergeLinii = (pixeli, latime, inaltime, lungimeMin = Math.round(latime / 25)) => {
  const rezultat = new Uint8ClampedArray(pixeli);
  const intunecat = (i) => 0.299 * pixeli[i] + 0.587 * pixeli[i + 1] + 0.114 * pixeli[i + 2] < PRAG_INTUNECAT;
  for (let y = 0; y < inaltime; y++) {
    let start = -1;
    for (let x = 0; x <= latime; x++) {
      const eIntunecat = x < latime && intunecat((y * latime + x) * 4);
      if (eIntunecat && start < 0) start = x;
      if (!eIntunecat && start >= 0) {
        if (x - start >= lungimeMin) {
          for (let k = start; k < x; k++) {
            const i = (y * latime + k) * 4;
            rezultat[i] = rezultat[i + 1] = rezultat[i + 2] = 255;
          }
        }
        start = -1;
      }
    }
  }
  return rezultat;
};

// verifica semnatura din zona indicata; intoarce true / false / null (nu s-a gasit eticheta)
const verificaSemnatura = (imagine, cuvinteOcr, eticheta, calculeazaZona) => {
  if (!eticheta) return null;
  const zona = calculeazaZona(eticheta, imagine.width, imagine.height);
  const cutiiText = cuvinteOcr.filter((c) => c.confidence > 60).map((c) => c.bbox);
  return procentCerneala(imagine.data, imagine.width, zona, cutiiText) > PRAG_SEMNATURA;
};

// linia citita de OCR care contine reperul (sau cea de dupa ea), pentru a fi aratata utilizatorului
export const linieCitita = (text, reper, deplasare) => {
  const linii = String(text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  const i = linii.findIndex((l) => normalizeaza(l).includes(reper));
  const linie = i >= 0 ? linii[i + deplasare] : null;
  if (!linie) return null;
  return linie.length > 140 ? linie.slice(0, 140) + '…' : linie;
};

/**
 * Evalueaza documentul si intoarce lista de rezultate { ok, mesaj }.
 * @param {object} p
 * @param {string} p.text textul recunoscut de OCR
 * @param {Array} p.cuvinteOcr cuvintele cu pozitii (cuvinteDinRezultat)
 * @param {{data: Uint8ClampedArray, width: number, height: number}} p.imagine prima pagina
 * @param {{nume: string, prenume: string}} p.student
 * @param {{nume: string, prenume: string}} p.profesor
 * @param {string} p.titlu
 * @param {'student'|'profesor'} p.rol cine incarca documentul
 */
export const evalueazaDocument = ({ text, cuvinteOcr, imagine, student, profesor, titlu, rol }) => {
  const rezultate = [];
  const adauga = (ok, mesaj) => rezultate.push({ ok, mesaj });

  if (normalizeaza(text).length < 30) {
    adauga(false, 'Nu am putut citi textul documentului. Verifică manual toate datele din previzualizare.');
    return rezultate;
  }

  const numeStudent = `${student.nume} ${student.prenume}`;
  adauga(
    contineNume(text, numeStudent),
    contineNume(text, numeStudent)
      ? `Numele studentului (${numeStudent}) apare în document.`
      : `Nu am găsit numele studentului (${numeStudent}) în document.`
  );

  if (profesor) {
    const numeProfesor = `${profesor.nume} ${profesor.prenume}`;
    const citit = linieCitita(text, 'coordonator stiintific', 0);
    adauga(
      contineNume(text, numeProfesor),
      contineNume(text, numeProfesor)
        ? `Numele profesorului coordonator (${numeProfesor}) apare în document.`
        : `Nu am găsit numele profesorului coordonator (${numeProfesor}) în document.` +
          (citit ? ` Am citit: „${citit}”.` : '')
    );
  }

  if (titlu) {
    const potrivire = potrivireTitlu(text, titlu);
    const citit = linieCitita(text, 'cu titlul', 1);
    adauga(
      potrivire >= PRAG_TITLU,
      potrivire >= PRAG_TITLU
        ? `Titlul „${titlu}” apare în document.`
        : `Titlul din document pare diferit de „${titlu}”.` + (citit ? ` Am citit: „${citit}”.` : '')
    );
  }

  const semnaturaStudent = verificaSemnatura(
    imagine, cuvinteOcr, gasesteEticheta(cuvinteOcr, 'semnatura', 'student'), zonaSemnaturaStudent
  );
  const semnaturaCoordonator = verificaSemnatura(
    imagine, cuvinteOcr, gasesteEticheta(cuvinteOcr, 'semnatura', 'coordonatorului'), zonaSemnaturaCoordonator
  );

  if (semnaturaStudent === null) {
    adauga(false, 'Nu am găsit spațiul pentru semnătura studentului. Verifică manual semnătura.');
  } else {
    adauga(
      semnaturaStudent,
      semnaturaStudent
        ? 'Există o semnătură în spațiul studentului.'
        : 'Spațiul pentru semnătura studentului pare gol.'
    );
  }

  if (rol === 'profesor') {
    if (semnaturaCoordonator === null) {
      adauga(false, 'Nu am găsit spațiul pentru semnătura coordonatorului. Verificați manual semnătura.');
    } else {
      adauga(
        semnaturaCoordonator,
        semnaturaCoordonator
          ? 'Există o semnătură în spațiul coordonatorului.'
          : 'Spațiul pentru semnătura coordonatorului pare gol.'
      );
    }
  } else if (semnaturaCoordonator) {
    // studentul trebuie sa trimita documentul semnat doar de el
    adauga(false, 'Pare să existe o semnătură în spațiul coordonatorului. Documentul trebuie semnat doar de tine.');
  }

  return rezultate;
};
